import type { Me } from '../api/queries/me';
import type { Tenant } from '../api/queries/tenant';
import type { PageKey } from '../shell/pages';

type Section = Me['sections'][number];
type Feature = Tenant['features'][number];

/**
 * Which of /api/me's sections opens each page, and the feature it needs, if any (ADR
 * 0016, ADR 0018). The API enforces access on its own; this decides what the web shows.
 */
export const ACCESS: Record<PageKey, { section: Section; feature?: Feature; hidden?: true }> = {
  pay: { section: 'pay' },
  payPending: { section: 'pendingPay' },
  paySearch: { section: 'pay', feature: 'line_item_search' },
  reps: { section: 'reps' },
  repsPending: { section: 'reps' },
  repsSearch: { section: 'reps', feature: 'line_item_search' },
  people: { section: 'people' },
  users: { section: 'users' },
  notices: { section: 'notices', feature: 'notices' },
  payDataHistory: { section: 'payData' },
  payDataPending: { section: 'payData' },
  products: { section: 'products', feature: 'products_admin' },
  settings: { section: 'settings' },
  // Hidden: a 404, not a 403, for anyone without the section, as the API does.
  operator: { section: 'operator', hidden: true },
};

/**
 * `allowed`, `forbidden` (the user's roles don't open it: a 403) or `off` (the tenant
 * turned the feature off, for everyone: a 404).
 */
export type Access = 'allowed' | 'forbidden' | 'off';

export function accessTo(
  page: PageKey,
  sections: readonly Section[],
  features: readonly Feature[],
): Access {
  const { section, feature, hidden } = ACCESS[page];
  if (feature && !features.includes(feature)) return 'off';
  if (sections.includes(section)) return 'allowed';
  return hidden ? 'off' : 'forbidden';
}
